import { notify } from '@affine/component';
import { AffineOtherPageLayout } from '@affine/component/affine-other-page-layout';
import { SignInPageContainer } from '@affine/component/auth-components';
import { GoldllenLoginLayout } from '@affine/core/components/goldllen-login/layout';
import { getLoginReturnPath } from '@affine/core/components/goldllen-login/return-path';
import { SignInPanel } from '@affine/core/components/sign-in';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  RouteLogic,
  useNavigateHelper,
} from '../../../components/hooks/use-navigate-helper';

export const SignIn = ({
  redirectUrl: redirectUrlFromProps,
  plain = false,
}: {
  redirectUrl?: string;
  plain?: boolean;
}) => {
  const t = useI18n();
  const navigate = useNavigate();
  const { jumpToIndex } = useNavigateHelper();
  const [searchParams] = useSearchParams();
  const redirectUrl = redirectUrlFromProps ?? searchParams.get('redirect_uri');

  const server = searchParams.get('server') ?? undefined;
  const error = searchParams.get('error');

  useEffect(() => {
    if (error) {
      notify.error({
        title: t['com.affine.auth.toast.title.failed'](),
        message: error,
      });
    }
  }, [error, t]);

  const handleClose = useCallback(() => {
    jumpToIndex(RouteLogic.REPLACE, {
      search: searchParams.toString(),
    });
  }, [jumpToIndex, searchParams]);

  const handleAuthenticated = useCallback(
    (status: AuthSessionStatus) => {
      if (status === 'authenticated') {
        if (redirectUrl) {
          if (redirectUrl.toUpperCase() === 'CLOSE_POPUP') {
            window.close();
            return;
          }
          Promise.resolve(
            navigate(
              !BUILD_CONFIG.isNative && !server
                ? getLoginReturnPath(
                    redirectUrl,
                    window.location.origin,
                    environment.subPath
                  )
                : redirectUrl,
              {
                replace: true,
              }
            )
          ).catch(console.error);
        } else {
          if (!BUILD_CONFIG.isNative && !server) {
            Promise.resolve(navigate('/app', { replace: true })).catch(
              console.error
            );
          } else {
            handleClose();
          }
        }
      }
    },
    [handleClose, navigate, redirectUrl, server]
  );

  const initStep = server ? 'addSelfhosted' : 'signIn';

  const panel = (
    <SignInPanel
      onSkip={handleClose}
      onAuthenticated={handleAuthenticated}
      initStep={initStep}
      server={server}
      redirectUrl={redirectUrl ?? undefined}
    />
  );

  if (plain) return panel;

  return (
    <SignInPageContainer>
      <div style={{ maxWidth: '400px', width: '100%', zIndex: 1 }}>{panel}</div>
    </SignInPageContainer>
  );
};

export const Component = () => {
  const [searchParams] = useSearchParams();
  if (!BUILD_CONFIG.isNative && !searchParams.has('server')) {
    return (
      <GoldllenLoginLayout>
        <SignIn plain />
      </GoldllenLoginLayout>
    );
  }
  return (
    <AffineOtherPageLayout>
      <SignIn />
    </AffineOtherPageLayout>
  );
};
